import html2pdf from 'html2pdf.js';
import moment from 'moment';
import { useEffect } from 'react';
import { config } from 'src/config';
import { useAppSelector } from 'src/rtk/app/hook';
import { useGetUserSlotInfoForTraineeQuery } from 'src/rtk/features/logbookTrainingSummary/logbookTrainingSummaryApi';
import { useGetDirectorAndSecretaryQuery } from 'src/rtk/features/setup/approval/approvalApi';

interface Props {
  personalProfile: any;
  onClose: () => void;
}

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const firstValue = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const formatDate = (value: unknown) =>
  value && moment(value as moment.MomentInput).isValid()
    ? moment(value as moment.MomentInput).format('DD-MMM-YYYY')
    : '';

const normalizeSubjectName = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const specialtyGroupBySubject: Record<string, string> = {
  'anaesthesiology': 'Anaesthesiology',
  'cardiology': 'Medicine',
  'endocrinology & metabolism': 'Medicine',
  'gastroenterology': 'Medicine',
  'hepatology': 'Medicine',
  'infectious disease & tropical medicine': 'Medicine',
  'medicine': 'Medicine',
  'nephrology': 'Medicine',
  'neurology': 'Medicine',
  'pulmonology': 'Medicine',
  'rheumatology': 'Medicine',
  'medical oncology': 'Medicine',
  'palliative medicine': 'Medicine',
  'feto-maternal medicine': 'Obstetrics & Gynaecology',
  'gynaecological oncology': 'Obstetrics & Gynaecology',
  'reproductive endocrinology & infertility': 'Obstetrics & Gynaecology',
  'obstetrics & gynaecology': 'Obstetrics & Gynaecology',
  'female pelvic medicine and reconstructive surgery': 'Obstetrics & Gynaecology',
  'neonatology': 'Paediatrics',
  'paediatric haematology & oncology': 'Paediatrics',
  'paediatric nephrology': 'Paediatrics',
  'paediatric gastroenterology & nutrition': 'Paediatrics',
  'paediatric neurology & development': 'Paediatrics',
  'paediatric pulmonology': 'Paediatrics',
  'paediatrics': 'Paediatrics',
  'paediatric cardiology': 'Paediatrics',
  'paediatric endocrinology & metabolism': 'Paediatrics',
  'paediatric endocrinology and metabolism': 'Paediatrics',
  'paediatric critical care medicine': 'Paediatrics',
  'cardiovascular surgery': 'Surgery',
  'neuro-surgery': 'Surgery',
  'orthopaedic surgery': 'Surgery',
  'paediatric surgery': 'Surgery',
  'plastic and reconstructive surgery': 'Surgery',
  'surgery': 'Surgery',
  'thoracic surgery': 'Surgery',
  'urology': 'Surgery',
  'hepatobiliary surgery': 'Surgery',
  'colorectal surgery': 'Surgery',
  'surgical oncology': 'Surgery',
  'casualty and emergency surgery': 'Surgery',
};

export const CoreTrainingCertificate = ({ personalProfile, onClose }: Props) => {
  const token = useAppSelector((state) => state.auth.token);
  const profile = personalProfile?.data?.details || {};

  const isMainCerticateEligible = String(personalProfile?.data?.core_certificate_status || '').trim().toLowerCase() === 'eligible';
  const isProvisionalCertificate = !isMainCerticateEligible && String(profile?.provision_certificate_status || '').trim().toLowerCase() === 'approve';

  const userId = personalProfile?.data?.id || '';

  const { data: slotData, isFetching } = useGetUserSlotInfoForTraineeQuery(userId, {
    skip: !userId,
  });

  const {
    data: signatoryData,
    isFetching: isFetchingSignatories,
  } = useGetDirectorAndSecretaryQuery();

  useEffect(() => {
    if (!userId || isFetching || isFetchingSignatories || !slotData || !signatoryData) return;

    const generateCertificate = async () => {
      const fetchImageBase64 = async (filepath: string) => {
        const response = await fetch(
          `${config.API_URL}/v1/get-image?filepath=${encodeURIComponent(filepath)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'image/*',
            },
          },
        );

        if (!response.ok) throw new Error('Unable to load signatory signature');

        const blob = await response.blob();
        const reader = new FileReader();

        return await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      };

      const signatories = Array.isArray(signatoryData.data)
        ? signatoryData.data
        : [signatoryData.data];
      const getSignatory = (userType: string) =>
        signatories.find(
          (entry) => entry?.user_type?.trim().toLowerCase() === userType.toLowerCase(),
        );
      const director = getSignatory('Director');
      const secretary = getSignatory('Secretary');
      const loadSignature = async (signatory?: DirectorAndSecretary) => {
        if (!signatory?.signature || typeof signatory.signature !== 'string') return '';

        try {
          return await fetchImageBase64(signatory.signature.replace(/^storage\//, ''));
        } catch {
          return '';
        }
      };
      const [directorSignature, secretarySignature] = await Promise.all([
        loadSignature(director),
        loadSignature(secretary),
      ]);

      const headerImage = new Image();
      headerImage.src = '/images/notice_header.png';

      const allSessions = slotData?.data?.log_entry_session || [];
      const coreSessions = allSessions
        .filter((session: any) =>
          String(session?.exam_training_info?.training_type || session?.slot_info?.training_type)
            .toLowerCase()
            .includes('core')
        )
        .slice(0, 4);

      const traineeName = firstValue(
        profile?.personal_info?.full_name,
        profile?.full_name
      );
      const subject = firstValue(
        profile?.subject?.subject_name,
        coreSessions[0]?.exam_training_info?.subject?.subject_name
      );
      const specialtyGroup = specialtyGroupBySubject[normalizeSubjectName(subject)];
      const passingSession = firstValue(profile?.fcps_part_one_passing_session);
      const passingYear = firstValue(
        profile?.fcps_part_one_passing_year
      );
      const bmdcNumber = firstValue(
        profile?.bmdc_registration_no
      );
      const certificateReference = firstValue(
        `BCPS/RTMD/CCCT/${profile?.bmdc_registration_no || ''}`
      );

      const rows = Array.from({ length: 4 }, (_, index) => {
        const session = coreSessions[index] || {};
        const supervisor = session?.supervisor_info?.personal_info || {};
        const employment = session?.supervisor_info?.current_employment_info || {};
        const trainingDuration = isProvisionalCertificate && index === 3
          ? `${escapeHtml(formatDate(session?.session_start_date))} - Till Date`
          : `${escapeHtml(formatDate(session?.session_start_date))}${session?.session_end_date ? ` - ${escapeHtml(formatDate(session?.session_end_date))}` : ''}`;

        return `
        <tr>
          <td class="block fw-bold text-start text-nowrap">Slot ${String(index + 1).padStart(2, '0')}</td>
          <td>${escapeHtml(firstValue(specialtyGroup, employment?.department_name))}</td>
          <td>${escapeHtml(session?.institute_name)}</td>
          <td>${escapeHtml(profile?.current_employment_info ? profile?.current_employment_info?.designation : 'Trainee')}</td>
          <td>${trainingDuration}</td>
          <td>${escapeHtml(firstValue('06 Months'))}</td>
          <td>${escapeHtml(supervisor?.full_name)}</td>
        </tr>`;
      }).join('');

      const html = `
      <style>
        body {
          margin: 0;
          color: #000 !important;
          background-color: #fff;
          font-family: "Times New Roman", Times, serif !important;
          font-size: 12pt !important;
        }
        .certificate-title { font-size: 15pt; }
        .training-title { font-size: 16pt; }
        .training-table { table-layout: fixed; font-size: 9.5pt; }
        .training-table th, .training-table td { color: #000 !important; }
        .training-table th { height: 23mm; line-height: 1.12; }
        .training-table td { height: 8mm; overflow-wrap: anywhere; }
        .certificate-qr { width: 25mm; height: 25mm; object-fit: contain; }
        .signatory-signature { width: 120px; height: 38px; object-fit: contain; }
        tr, .signatories { break-inside: avoid; page-break-inside: avoid; }
      </style>

      <div class="px-5">
        <div class="d-flex justify-content-between mb-4 small">
          <div><strong>Certificate Reference No:</strong> ${escapeHtml(certificateReference)}</div>
          <div><strong>Date:</strong> ${moment().format('DD/MMM/YYYY')}</div>
        </div>

        <div class="certificate-title text-center fw-bold mb-4">
          CERTIFICATE OF COMPLETION OF CORE TRAINING (CCCT)
          ${isProvisionalCertificate ? '<div class="mt-1">(Provisional)</div>' : ''}
        </div>

        <p class="lh-base mb-4" style="text-align: justify;">
          This is to certify that Dr. <strong>${escapeHtml(traineeName)}</strong> (FCPS Part-I:
          <strong>${escapeHtml(subject)}</strong>, ${escapeHtml(passingSession)} ${escapeHtml(passingYear)}),
          BMDC Registration No: <strong>${escapeHtml(bmdcNumber)}</strong> has successfully fulfilled all
          clinical rotations, competencies, and training requirements prescribed under the curriculum
          guidelines of the Bangladesh College of Physicians and Surgeons (BCPS) in accordance with the
          verified records of the electronic logbook.
        </p>

        <div class="training-title text-center fw-bold mb-3">TRAINING RECORD</div>

        <table class="training-table table table-sm table-bordered border-dark text-center align-middle mb-0">
          <thead><tr>
            <th style="width: 11%;">Rotation<br/>Block</th>
            <th style="width: 17%;">Specialty</th>
            <th style="width: 17%;">Training<br/>Institute/<br/>Hospital</th>
            <th style="width: 11%;">Training<br/>Post</th>
            <th style="width: 15%;">Duration<br/>(From - To)</th>
            <th style="width: 13%;">Completed<br/>Tenure</th>
            <th style="width: 16%;">Name<br/>of the<br/>Supervisor</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="text-center fw-bold fs-5 mt-3 mb-5">
          Cumulative Total Duration of Training: 24 Months
        </div>

        <div class="signatories row text-center mt-5">
          <div class="col-6">
            ${directorSignature
          ? `<img class="signatory-signature" src="${directorSignature}" alt="Director signature" />`
          : '<div class="signatory-signature mx-auto"></div>'}
            <div class="fw-bold">(${escapeHtml(director?.name)})</div>
            <div>${escapeHtml(director?.user_type)}</div>
            <div>Research &amp; Training Monitoring Dept (RTMD)</div>
          </div>
          <div class="col-6">
            ${secretarySignature
          ? `<img class="signatory-signature" src="${secretarySignature}" alt="Secretary signature" />`
          : '<div class="signatory-signature mx-auto"></div>'}
            <div class="fw-bold">(${escapeHtml(secretary?.name)})</div>
            <div>${escapeHtml(secretary?.user_type)}</div>
            <div>Bangladesh College of Physicians and Surgeons (BCPS)</div>
          </div>
        </div>
      </div>`;

      const options = {
        margin: [42, 5, 5, 5],
        image: { type: 'png', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      html2pdf()
        .from(html)
        .set(options)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          const totalPages = pdf.internal.getNumberOfPages();

          for (let page = 1; page <= totalPages; page++) {
            pdf.setPage(page);
            pdf.addImage(
              headerImage,
              'PNG',
              0,
              0,
              pdf.internal.pageSize.getWidth(),
              35
            );
          }

          window.open(pdf.output('bloburl'), '_blank');
        })
        .finally(onClose);
    };

    generateCertificate();
  }, [
    isFetching,
    isFetchingSignatories,
    isProvisionalCertificate,
    onClose,
    personalProfile,
    signatoryData,
    slotData,
    token,
    userId,
  ]);

  return null;
};

